import type { Signal } from '@angular/core';
import { computed } from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';

/**
 * Minimal envelope shape a widget entity must satisfy to install
 * {@link legacyWidgetEnvelopePlugin}. Matches the legacy widget envelope produced
 * by the host app (`id`/`name`/`columnId` plus the optional `displayId` the
 * user can set and the optional variant discriminator `type`).
 */
export type LegacyWidgetEnvelopeData = {
    id: string;
    name: string;
    columnId: string;
    displayId?: string | null;
    type?: string;
};

export type LegacyWidgetEnvelopeApi<E extends LegacyWidgetEnvelopeData = LegacyWidgetEnvelopeData> = {
    readonly name: FieldTree<E['name']>;
    readonly columnId: FieldTree<E['columnId']>;
    /**
     * Envelope discriminator. **Beware:** persisted default-variant entities
     * omit the `type` key entirely, so on those forms the FieldTree child
     * never materialises and calling `form.type()` throws — read
     * `form.value().type` when checking the variant. Writes are not
     * supported — the variant is fixed at form-construction time.
     */
    readonly type: FieldTree<E['type']>;
    /**
     * Stable element id for DOM attributes — the `displayId` if the user has
     * set one, otherwise `widget-${id}`. A computed signal so any template
     * binding re-renders when displayId is updated through the form.
     */
    displayId: Signal<string>;
    setDisplayId(id: string): void;
};

/**
 * Envelope accessors shared by every widget form (`name`/`columnId`/`type`
 * FieldTree references + the `widgetId` computed). Receives the form's root
 * FieldTree **lazily** (`() => _raw`) and exposes getters, so tree paths only
 * materialise on consumer access — compose with
 * {@link import('../../core/compose-form').composeForm}, never object spread.
 */
export function legacyWidgetEnvelopePlugin<E extends LegacyWidgetEnvelopeData>(
    root: () => FieldTree<E>
): LegacyWidgetEnvelopeApi<E> {
    // FieldTree is invariant (writable signals), so the narrowing is done via
    // an explicit cast — safe by the `E extends LegacyWidgetEnvelopeData` constraint.
    const r = root as unknown as () => FieldTree<LegacyWidgetEnvelopeData>;
    return {
        get name() {
            return r().name as FieldTree<E['name']>;
        },
        get columnId() {
            return r().columnId as FieldTree<E['columnId']>;
        },
        get type() {
            return r().type as FieldTree<E['type']>;
        },
        displayId: computed(() => {
            const value = r()().value();
            return value.displayId ? value.displayId : `widget-${value.id ?? ''}`;
        }),
        /**
         * Written through the ROOT node, not `r().displayId()`: `displayId` is
         * optional on every widget schema and no factory seeds it, so on a widget
         * whose id has never been set the child field does not exist and a direct
         * write would throw. Updating the parent materialises the key.
         */
        setDisplayId(id: string) {
            r()().value.update((value) => ({ ...value, displayId: id }));
        },
    };
}
